import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as Sentry from '@sentry/browser';
import { useHistory } from 'react-router';
import qs from 'query-string';
import styled from 'styled-components';
import { checkSession, loginSuccess, setAuth } from 'modules/postLoginActionsTable';
import { rehydrateUserData } from 'modules/app';
import { getAuthHelper } from 'utils/auth';
import {
  ConsumerAuthHelper,
  OKTA_ASSOCIATION_REDIRECT_URL,
  OKTA_POST_LOGIN_REDIRECT,
  OKTA_PRESERVE_POST_SIGNUP_REDIRECT,
} from 'utils/auth/consumer';
import { Button, ButtonEnums, Loader } from 'components/atoms';
import { setSiteEntry } from 'modules/startup';
import { ProvisioningScreenState, useProvisioningStatus } from 'hooks/useProvisioningStatus';
import { CIMA_MEMBERSHIP_ROUTES, Routes } from 'constants/index';
import { getPath } from 'utils';
import { Utils } from 'mxp-utils';

type WaitingRoomContext = {
  authHelper: ConsumerAuthHelper;
};

const isExcludedFromOnboardingRedirect = (redirectTo: string | null): boolean => {
  if (!redirectTo) {
    return false;
  }

  const redirectPath = qs.parseUrl(redirectTo).url;
  if (redirectPath === getPath(Routes.EXEMPTION_CALCULATOR_RESULT)) {
    return true;
  }

  return Object.values(CIMA_MEMBERSHIP_ROUTES).some(route => route.path === redirectPath);
};

const shouldPreservePostSignupRedirect = (): boolean =>
  Boolean(sessionStorage.getItem(OKTA_PRESERVE_POST_SIGNUP_REDIRECT));

const setPostLoginRedirectToOnboarding = () => {
  const redirectTo = sessionStorage.getItem(OKTA_POST_LOGIN_REDIRECT);
  if (shouldPreservePostSignupRedirect() || isExcludedFromOnboardingRedirect(redirectTo)) {
    return;
  }

  sessionStorage.setItem(OKTA_POST_LOGIN_REDIRECT, getPath(Routes.CIMA_ONBOARDING_PRACTICE_AREA_PREFERENCES));
};

type WaitingRoomScreenProps = {
  message: string;
  onRetry?: () => void;
  screenState: ProvisioningScreenState;
};

const WaitingRoomScreen: React.FC<WaitingRoomScreenProps> = ({ message, onRetry, screenState }) => {
  const isLoading = screenState === 'provisioning' || screenState === 'refreshing';
  const loadingWaitingRoomTitle = screenState === 'refreshing' ? 'Finalizing your account' : 'Setting up your account';
  const waitingRoomTitle = isLoading ? loadingWaitingRoomTitle : '';

  return (
    <WaitingRoomContainer>
      <WaitingRoomCard>
        <WaitingRoomAccent />
        <WaitingRoomContent>
          {isLoading ? <WaitingRoomSpinner aria-hidden="true" /> : <WaitingRoomStatusIcon>!</WaitingRoomStatusIcon>}
          {waitingRoomTitle && <WaitingRoomTitle>{waitingRoomTitle}</WaitingRoomTitle>}
          {message && <WaitingRoomStatus>{message}</WaitingRoomStatus>}
          {isLoading && (
            <WaitingRoomHint>Please do not refresh or go back while we finish setting up your account.</WaitingRoomHint>
          )}
        </WaitingRoomContent>
        {!isLoading && onRetry && (
          <RetryButton onClick={onRetry} testId="provisioning-retry" variant={ButtonEnums.variants.primary}>
            Try Again
          </RetryButton>
        )}
      </WaitingRoomCard>
    </WaitingRoomContainer>
  );
};

export const LoginCallback: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [waitingRoomContext, setWaitingRoomContext] = useState<WaitingRoomContext | null>(null);

  const completeLogin = useCallback(
    async (associationRedirectUrl?: string) => {
      await dispatch(setAuth(true));
      const checkSessionAction = await dispatch(checkSession());
      const { session }: { session: string } = checkSessionAction.payload;
      dispatch(rehydrateUserData(true, session));
      dispatch(loginSuccess({}));

      const sessionStoredAssociationRedirectUrl = sessionStorage.getItem(OKTA_ASSOCIATION_REDIRECT_URL) || '';
      const validatedAssociationRedirectUrl = Utils.getValidatedAssociationRedirectUrl(
        associationRedirectUrl ?? sessionStoredAssociationRedirectUrl
      );
      const preservePostSignupRedirect = shouldPreservePostSignupRedirect();

      if (!preservePostSignupRedirect && validatedAssociationRedirectUrl) {
        sessionStorage.removeItem(OKTA_ASSOCIATION_REDIRECT_URL);
        sessionStorage.removeItem(OKTA_POST_LOGIN_REDIRECT);
        sessionStorage.removeItem(OKTA_PRESERVE_POST_SIGNUP_REDIRECT);
        window.location.assign(validatedAssociationRedirectUrl);
        return;
      }

      const redirectTo = sessionStorage.getItem(OKTA_POST_LOGIN_REDIRECT) ?? '/';
      const { query: queryParams } = qs.parseUrl(redirectTo);
      dispatch(setSiteEntry({ page: redirectTo, params: queryParams }));
      sessionStorage.removeItem(OKTA_ASSOCIATION_REDIRECT_URL);
      sessionStorage.removeItem(OKTA_POST_LOGIN_REDIRECT);
      sessionStorage.removeItem(OKTA_PRESERVE_POST_SIGNUP_REDIRECT);
      history.replace(redirectTo);
    },
    [dispatch, history]
  );

  const handleSessionExpired = useCallback(() => {
    sessionStorage.removeItem(OKTA_ASSOCIATION_REDIRECT_URL);
    sessionStorage.removeItem(OKTA_PRESERVE_POST_SIGNUP_REDIRECT);
    history.replace('/login');
  }, [history]);

  const { message, retry, screenState } = useProvisioningStatus({
    enabled: Boolean(waitingRoomContext),
    authHelper: waitingRoomContext?.authHelper ?? null,
    onComplete: completeLogin,
    onSessionExpired: handleSessionExpired,
  });

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const authHelper = getAuthHelper();
        if (!(authHelper instanceof ConsumerAuthHelper)) {
          throw new Error('Login callback is only valid for ConsumerAuthHelper');
        }

        await authHelper.handleRedirectCallbackAndStoreTokens();
        const claims = await authHelper.getProvisioningClaims();
        const oktaUserId = claims?.uid;

        if (!oktaUserId) {
          throw new Error('Unable to determine Okta user id from token claims');
        }

        if (!claims?.AICPAUID) {
          setPostLoginRedirectToOnboarding();
          setWaitingRoomContext({ authHelper });
          return;
        }

        await completeLogin();
      } catch (err) {
        console.error('LoginCallback failed:', err);
        Sentry.captureException(err);
        sessionStorage.removeItem(OKTA_ASSOCIATION_REDIRECT_URL);
        sessionStorage.removeItem(OKTA_PRESERVE_POST_SIGNUP_REDIRECT);
        history.replace('/login');
      }
    };

    handleCallback();
  }, [completeLogin, history]);

  if (waitingRoomContext) {
    return <WaitingRoomScreen message={message} onRetry={retry} screenState={screenState} />;
  }

  return <Loader active content="Loading" />;
};

const WaitingRoomContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.pxToRem(24)};
  background: radial-gradient(circle at top, rgba(91, 55, 183, 0.1), transparent 28%),
    linear-gradient(
      180deg,
      ${props => props.theme.colors.neutralGrey1} 0%,
      ${props => props.theme.colors.neutralWhite} 42%
    );
`;

const WaitingRoomCard = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: ${props => props.theme.pxToRem(560)};
  padding: ${props => props.theme.pxToRem(48)} ${props => props.theme.pxToRem(40)};
  border: ${props => props.theme.pxToRem(1)} solid ${props => props.theme.colors.neutralGrey3};
  border-radius: ${props => props.theme.pxToRem(20)};
  box-shadow: 0 ${props => props.theme.pxToRem(20)} ${props => props.theme.pxToRem(56)} rgba(27, 31, 59, 0.12);
  text-align: center;
  background: ${props => props.theme.colors.neutralWhite};
`;

const RetryButton = styled(Button)`
  &&&& {
    min-width: ${props => props.theme.pxToRem(160)};
  }
`;

const WaitingRoomAccent = styled.div`
  position: absolute;
  inset: 0 auto auto 0;
  width: 100%;
  height: ${props => props.theme.pxToRem(6)};
  background: linear-gradient(
    90deg,
    ${props => props.theme.colors.primaryPurple} 0%,
    ${props => props.theme.colors.primaryLightPurple} 100%
  );
`;

const WaitingRoomContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${props => props.theme.pxToRem(14)};
  margin-bottom: ${props => props.theme.pxToRem(20)};
`;

const WaitingRoomSpinner = styled.div`
  width: ${props => props.theme.pxToRem(56)};
  height: ${props => props.theme.pxToRem(56)};
  border-radius: 50%;
  border: ${props => props.theme.pxToRem(4)} solid rgba(91, 55, 183, 0.12);
  border-top-color: ${props => props.theme.colors.primaryPurple};
  animation: waiting-room-spin 0.6s linear infinite;

  @keyframes waiting-room-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const WaitingRoomStatusIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${props => props.theme.pxToRem(56)};
  height: ${props => props.theme.pxToRem(56)};
  border-radius: 50%;
  background: rgba(91, 55, 183, 0.08);
  color: ${props => props.theme.colors.primaryPurple};
  font-size: ${props => props.theme.pxToRem(28)};
  font-weight: 700;
`;

const WaitingRoomTitle = styled.h1`
  margin: 0;
  color: ${props => props.theme.colors.neutralBlack};
  font-size: ${props => props.theme.pxToRem(30)};
  font-weight: 600;
  line-height: 1.2;
`;

const WaitingRoomStatus = styled.p`
  margin: 0;
  color: ${props => props.theme.colors.neutralGrey8};
  font-size: ${props => props.theme.pxToRem(20)};
  line-height: 1.5;
  max-width: ${props => props.theme.pxToRem(420)};
`;

const WaitingRoomHint = styled.p`
  margin: 0;
  color: ${props => props.theme.colors.neutralGrey6};
  font-size: ${props => props.theme.pxToRem(14)};
  line-height: 1.5;
  max-width: ${props => props.theme.pxToRem(360)};
`;
